/**
 * An organization's brand colour, applied as the app's primary colour.
 *
 * The API only ever stores a colour white text is readable on (4.5:1), so the brand colour can take
 * `--color-primary`'s place everywhere: filled buttons, links, the nav rail and the focus ring. The
 * shades `index.css` hard-codes for the navy are derived here instead, and the rail's muted text
 * falls back to white when the navy's muted grey would be too faint on the new colour.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';

/** The minimum contrast white text needs on the brand colour, the same rule the API enforces. */
export const MIN_CONTRAST_WITH_WHITE = 4.5;

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

/** `#RRGGBB` upper case (`#abc` expands), or null when the value is not a hex colour. */
export const normalizeHex = (value: string | null | undefined): string | null => {
  const match = HEX.exec((value ?? '').trim());
  if (!match) return null;
  const digits = match[1].toUpperCase();
  return digits.length === 3
    ? `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`
    : `#${digits}`;
};

const toRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = ([r, g, b]: [number, number, number]): string =>
  `#${[r, g, b].map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

const luminance = (hex: string): number => {
  const [r, g, b] = toRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two `#RRGGBB` colours. */
export const contrastRatio = (a: string, b: string): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

export const contrastWithWhite = (hex: string): number => contrastRatio(hex, '#FFFFFF');

/**
 * The page background (`--color-off-white`). The brand colour is link and label text on it, and it is the darker of
 * the two backgrounds that text sits on. The API checks the same two backgrounds.
 */
export const PAGE_BACKGROUND = '#F3F5F9';

/**
 * The lower of the two contrasts that matter for a primary colour: white text on it (buttons) and it as text on the
 * page background (links). It must reach 4.5:1.
 */
export const primaryContrast = (hex: string): number =>
  Math.min(contrastWithWhite(hex), contrastRatio(hex, PAGE_BACKGROUND));

/** Mix `hex` toward `target` by `amount` (0 to 1). */
const mix = (hex: string, target: string, amount: number): string => {
  const a = toRgb(hex);
  const b = toRgb(target);
  return toHex([0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * amount) as [number, number, number]);
};

/**
 * A pale tint of the brand for a background. Brand-coloured text sits on these (a selected row, a chip), so the
 * tint is lightened further until that text still reaches 4.5:1. White always does, since the brand passes on it.
 */
const wash = (brand: string, amount: number): string => {
  for (let a = amount; a < 1; a += 0.01) {
    const tint = mix(brand, '#FFFFFF', a);
    if (contrastRatio(brand, tint) >= MIN_CONTRAST_WITH_WHITE) return tint;
  }
  return '#FFFFFF';
};

/** The CSS variables a brand colour sets. Exported for tests. */
export const brandThemeVariables = (brand: string): Record<string, string> => {
  const muted = contrastRatio('#C9D4EA', brand) >= MIN_CONTRAST_WITH_WHITE ? '#C9D4EA' : '#FFFFFF';
  return {
    '--color-primary': brand,
    '--color-primary-hover': mix(brand, '#000000', 0.25),
    '--color-primary-pressed': mix(brand, '#000000', 0.45),
    '--color-primary-subtle': wash(brand, 0.9),
    '--color-primary-light': wash(brand, 0.9),
    '--color-primary-lighter': wash(brand, 0.75),
    '--color-primary-wash': wash(brand, 0.95),
    '--color-on-primary-muted': muted,
  };
};

const APPLIED_KEYS = Object.keys(brandThemeVariables('#000000'));

/**
 * Sets (or, for null or an unreadable colour, clears) the brand colour on the document root. A colour
 * white text cannot be read on is ignored, so a bad value can never make the app's buttons unreadable.
 */
export const applyBrandTheme = (color: string | null | undefined, root: HTMLElement = document.documentElement): void => {
  const brand = normalizeHex(color);
  if (!brand || primaryContrast(brand) < MIN_CONTRAST_WITH_WHITE) {
    APPLIED_KEYS.forEach((key) => root.style.removeProperty(key));
    return;
  }
  Object.entries(brandThemeVariables(brand)).forEach(([key, value]) => root.style.setProperty(key, value));
};

/**
 * The API sends the logo as a path on itself (`api/public/org-logos/…`). This makes it absolute.
 * Anything that is not that path is ignored rather than loaded.
 */
export const resolveOrganizationLogoUrl = (path: string | null | undefined): string | null => {
  if (!path || !/^\/?api\/public\/org-logos\/[0-9a-f-]{36}(\?v=\d+)?$/i.test(path)) return null;
  return `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
};
